import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity } from "../context/EffectiveIdentityContext";
import { auth, db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { Send, Bot, User, Loader2 } from "lucide-react";
import { sanitizeHTML } from "../utils/sanitizeHTML";
import { devLog } from "../utils/devLog";
import { filterPaymentsVisibleToResidents } from "../utils/cierrePublicacion";
import { tolerantId } from "../utils/firestoreHelpers";
import { leerPagosParaResidente } from "../utils/consultasEdificio";
import { leerFichaResidente } from "../utils/fichaResidente";
import { asambleaPropietario } from "../utils/asambleas/api";
import { useIsMobile } from "../hooks/useResponsive";
import { AppHeader, AppButton, Badge, IconBox, SectionLabel } from "../components/app";
import { appColors, appType, appSpacing, appShadows, appRadius } from "../utils/appTheme";

// Sugerencias iniciales según el rol: el residente pregunta por su cuenta;
// el staff (admin/superadmin) por la gestión del edificio.
// Se guardan como claves i18n y se resuelven con t() dentro del componente.
const RESIDENT_SUGGESTION_KEYS = [
  "suggestions.resident.debt",
  "suggestions.resident.meeting",
  "suggestions.resident.report",
  "suggestions.resident.admin",
];
const STAFF_SUGGESTION_KEYS = [
  "suggestions.staff.delinquency",
  "suggestions.staff.paymentsToVerify",
  "suggestions.staff.openIssues",
  "suggestions.staff.residentQueries",
];

const Habi = ({ setSelected, goBack, userType, previewAsAdmin } = {}) => {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation("habi");
  // Staff = admin (1), superadmin (2) o superadmin viendo como admin.
  const isStaff = userType === 1 || userType === 2 || previewAsAdmin === true;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);
  // Corte definitivo, sin más reintentos: "noAccess" (el servidor rechazó el
  // edificio) o "failed" (se agotaron los reintentos).
  const [stopped, setStopped] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  // Modo demo: el server (WS) envia messages_remaining en cada respuesta y, al
  // pasar el limite, manda demo_limit_reached: true. Mostramos un chip y, en el
  // limite, un boton "Contactar asesor" arriba del input.
  const [messagesRemaining, setMessagesRemaining] = useState(null);
  const [demoLimitReached, setDemoLimitReached] = useState(false);
  const [contactingAdvisor, setContactingAdvisor] = useState(false);
  // E2 — Sugerencias dinámicas: se actualizan según el estado real del
  // residente (deuda, incidencias, próxima asamblea). Defaults útiles si
  // no hay contexto cargado todavía.
  const [suggestions, setSuggestions] = useState(
    (isStaff ? STAFF_SUGGESTION_KEYS : RESIDENT_SUGGESTION_KEYS).map((k) => t(k))
  );


  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const contextRef = useRef(null);

  const { selectedBuilding } = useBuilding();
  // viendoComo: "Ver como residente" (quien mira es staff y lee el edificio entero).
  const { email: effEmail, userid: effUserid, readOnly: viendoComo } = useEffectiveIdentity();

  // Fetch building context data for Habi enrichment
  useEffect(() => {
    const fetchContext = async () => {
      if (!selectedBuilding?.buildingid) {
        contextRef.current = null;
        return;
      }
      // El staff no arma contexto de residente (deuda/incidencias propias);
      // sus sugerencias son de gestión y sus datos vienen de las tools del backend.
      if (isStaff) {
        contextRef.current = null;
        setSuggestions(STAFF_SUGGESTION_KEYS.map((k) => t(k)));
        return;
      }
      try {
        const buildingId = Number(selectedBuilding.buildingid);
        const userId = auth.currentUser?.uid || "";

        let effectiveUserId = userId;
        // Ficha del residente (en "ver como", del contexto: quien mira es staff
        // y con C10 no lista users por email).
        const ficha = await leerFichaResidente(db, { email: effEmail, userid: effUserid, viendoComo });
        if (ficha) effectiveUserId = String(ficha.userid);

        const apartmentsSnap = await getDocs(
          query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId)))
        );
        const visiblePaymentUserIds = new Set([String(effectiveUserId)]);
        apartmentsSnap.docs.forEach((d) => {
          const apt = d.data();
          if (String(apt.ownerSecondary) === String(effectiveUserId) && apt.owner != null && String(apt.owner).trim() !== "") {
            visiblePaymentUserIds.add(String(apt.owner));
          }
          if (String(apt.tenantSecondary) === String(effectiveUserId) && apt.tenant != null && String(apt.tenant).trim() !== "") {
            visiblePaymentUserIds.add(String(apt.tenant));
          }
        });

        // Pagos visibles para este usuario (propio + principal si es secundario).
        // El ex-ocupante solo lee los suyos (reglas 0D): si la consulta por
        // edificio se niega, leerPagosParaResidente la repite fijando sus
        // userid. En "ver como" no hay segundo intento (quien mira es staff).
        const allBuildingPayments = await leerPagosParaResidente(db, buildingId, {
          uid: viendoComo ? null : auth.currentUser?.uid || null,
        });
        const ownPayments = allBuildingPayments.filter(
          (p) => String(p.userid) === String(effectiveUserId)
        );
        const payments = filterPaymentsVisibleToResidents(
          ownPayments.length > 0
            ? ownPayments
            : allBuildingPayments.filter((p) => visiblePaymentUserIds.has(String(p.userid)))
        );
        const pendingPayments = payments.filter((p) => (p.status || "").toLowerCase() !== "pagado" && (p.status || "").toLowerCase() !== "paid");
        const totalPending = pendingPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
        const lastPaid = payments
          .filter((p) => (p.status || "").toLowerCase() === "pagado" || (p.status || "").toLowerCase() === "paid")
          .sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0))[0];

        // Fetch incidents for this user
        const incidentsSnap = await getDocs(query(
          collection(db, "incidents"),
          where("buildingid", "in", tolerantId(buildingId)),
          where("userid", "in", tolerantId(effectiveUserId))
        ));
        const incidents = incidentsSnap.docs.map((d) => d.data());
        const openIncidents = incidents.filter((i) => (i.status || "").toLowerCase() !== "resuelto" && (i.status || "").toLowerCase() !== "resolved");

        // E2 — Próxima asamblea del edificio (para sugerencia dinámica). Las
        // asambleas son de los propietarios: solo se piden si es propietario
        // vigente de una unidad del edificio (owner/ownerSecondary, la misma
        // regla que BuildingContext y el servidor), y por el callable. La
        // colección vieja `meetings` quedó cerrada a los clientes (guardaba
        // transcripciones de la herramienta vieja de actas).
        let nextMeeting = null;
        const uidNum = Number(effectiveUserId);
        const esMio = (v) =>
          v != null && String(v).trim() !== "" &&
          (String(v) === String(effectiveUserId) || (!isNaN(uidNum) && Number(v) === uidNum));
        const esPropietarioVigente = apartmentsSnap.docs.some((d) => {
          const apt = d.data();
          return esMio(apt.owner) || esMio(apt.ownerSecondary);
        });
        if (esPropietarioVigente) {
          try {
            const res = await asambleaPropietario("listar", { buildingId: selectedBuilding.buildingid });
            const now = Date.now();
            nextMeeting = ((res && res.asambleas) || [])
              .filter((a) => a && a.estado === "convocada" && Number(a.inicioMs) > now)
              .map((a) => ({
                title: a.tipo === "extraordinaria" ? "Asamblea extraordinaria" : "Asamblea ordinaria",
                _ts: Number(a.inicioMs),
              }))
              .sort((a, b) => a._ts - b._ts)[0] || null;
          } catch (e) {
            // Sin la sugerencia de asamblea; el resto del contexto sigue.
            devLog("Habi: no se pudo cargar la próxima asamblea", e && e.code);
          }
        }

        // Build context summary
        contextRef.current = {
          building: {
            name: selectedBuilding.name || selectedBuilding.nombre || "",
            address: selectedBuilding.address || "",
            city: selectedBuilding.city || "",
          },
          financial: {
            totalPending,
            pendingCount: pendingPayments.length,
            lastPaidAmount: lastPaid ? Number(lastPaid.amount || 0) : null,
            lastPaidDate: lastPaid?.date?.seconds ? new Date(lastPaid.date.seconds * 1000).toLocaleDateString("es-UY") : null,
          },
          incidents: {
            openCount: openIncidents.length,
            totalCount: incidents.length,
            recent: openIncidents.slice(0, 3).map((i) => ({
              description: i.description || i.descripcion || "",
              status: i.status || i.estado || "",
            })),
          },
          nextMeeting: nextMeeting ? {
            title: nextMeeting.title || nextMeeting.tema || "Asamblea",
            ts: nextMeeting._ts,
            daysAway: Math.max(1, Math.round((nextMeeting._ts - Date.now()) / (1000 * 60 * 60 * 24))),
          } : null,
        };

        // E2 — Generar sugerencias dinámicas priorizadas por relevancia.
        const dyn = [];
        // 1. Deuda pendiente → más prioritaria
        if (totalPending > 0) {
          const formatted = totalPending.toLocaleString("es-UY", {
            maximumFractionDigits: 0,
          });
          dyn.push(t("suggestions.dynamic.debt", { amount: formatted }));
        }
        // 2. Incidencia abierta hace tiempo
        const staleIncidents = openIncidents.filter((i) => {
          const ts = i.createdAt?.seconds || i.date?.seconds || 0;
          if (!ts) return false;
          const daysOld = (Date.now() / 1000 - ts) / (60 * 60 * 24);
          return daysOld >= 5;
        });
        if (staleIncidents.length > 0) {
          const incTitle = staleIncidents[0].description || staleIncidents[0].descripcion || "mi incidencia";
          dyn.push(t("suggestions.dynamic.staleIncident", { title: String(incTitle).slice(0, 40) }));
        } else if (openIncidents.length > 0) {
          dyn.push(t("suggestions.dynamic.openIncidents"));
        }
        // 3. Asamblea próxima
        if (nextMeeting) {
          const days = Math.max(1, Math.round((nextMeeting._ts - Date.now()) / (1000 * 60 * 60 * 24)));
          if (days <= 14) {
            dyn.push(t("suggestions.dynamic.nextAssembly", { days }));
          }
        }
        // 4. Default extra para llegar a 4: depende de qué falta
        if (dyn.length < 4) {
          if (totalPending === 0) dyn.push(t("suggestions.dynamic.dueDate"));
          if (!nextMeeting) dyn.push(t("suggestions.resident.meeting"));
          if (openIncidents.length === 0) dyn.push(t("suggestions.resident.report"));
          dyn.push(t("suggestions.resident.admin"));
          dyn.push(t("suggestions.dynamic.pet"));
        }
        setSuggestions(dyn.slice(0, 4));
      } catch (error) {
        console.error("Error fetching Habi context:", error);
        contextRef.current = null;
      }
    };
    fetchContext();
  }, [selectedBuilding, isStaff, effEmail, effUserid, viendoComo, t]);

  // Scroll automático al último mensaje
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Conectar WebSocket
  useEffect(() => {
    let cancelled = false;
    // Edificio o identidad nuevos: los reintentos y los cortes arrancan de cero.
    reconnectAttemptsRef.current = 0;
    setStopped(null);
    setError(null);

    const addErrorBubble = (content) => {
      // Sin repetir el mismo error en cada reintento de conexión.
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.isError && last.content === content) return prev;
        return [...prev, { id: Date.now(), role: "assistant", content, isError: true }];
      });
    };

    const connectWebSocket = async () => {
      if (cancelled) return;
      const user = auth.currentUser;
      if (!user || !selectedBuilding) {
        setError("errors.needLogin");
        return;
      }

      // Identidad efectiva: en "ver como residente" conectamos el WS como el
      // residente impersonado (userId/email), pero el token de auth sigue siendo
      // el del usuario Firebase logueado (admin) — ver getIdToken() más abajo.
      const userId = effUserid || user.uid;
      const buildingId = selectedBuilding.buildingid || selectedBuilding.id;
      const userEmail = encodeURIComponent(effEmail || user.email || "");

      // Obtener Firebase ID token para autenticar el WebSocket en el backend.
      // Sin esto, el server cierra la conexion con "Se requiere autenticacion".
      let token = "";
      try {
        token = await user.getIdToken();
      } catch (e) {
        console.error("Habi: error obteniendo token Firebase:", e);
        setError("errors.authFailed");
        return;
      }
      if (cancelled) return;

      // URL del WebSocket - Cloud Run en producción
      // Para desarrollo local con servidor local, usar: ws://localhost:8000
      const wsUrl = process.env.REACT_APP_PROPIA_WS_URL || "wss://propia-agent-241529496588.us-central1.run.app";
      const fullUrl = `${wsUrl}/ws/chat/${userId}/${buildingId}/${userEmail}?token=${encodeURIComponent(token)}&language=${encodeURIComponent(i18n.language || "es")}`;

      devLog("Conectando a Habi");

      try {
        const ws = new WebSocket(fullUrl);
        wsRef.current = ws;
        // Hasta "connected" el servidor todavía verifica el acceso: un error
        // en ese tramo se decide al cerrar (ver onclose).
        let ready = false;
        let pendingError = null;

        ws.onopen = () => {
          devLog("Habi WebSocket abierto");
        };

        ws.onmessage = (event) => {
          if (cancelled || wsRef.current !== ws) return;
          try {
            const data = JSON.parse(event.data);
            devLog("Habi mensaje:", data);

            if (data.type === "connected") {
              // Conectado de verdad: acceso verificado y agente listo. Recién
              // acá se reinician los reintentos (si se reiniciaran al abrir, un
              // rechazo del servidor reintentaría para siempre cada 3 s).
              // No autosaludar: el empty state con los chips de sugerencias
              // queda visible hasta la primera consulta; el historial (si
              // existe) llega por separado vía "history".
              ready = true;
              reconnectAttemptsRef.current = 0;
              setIsConnected(true);
              setHasConnectedOnce(true);
              setError(null);
            } else if (data.type === "history") {
              // Hidratacion del historial conversacional desde Firestore
              const hydrated = (data.messages || []).map((m, idx) => ({
                id: `hist-${idx}-${m.timestamp || idx}`,
                role: m.role === "assistant" ? "assistant" : "user",
                content: m.content || ""
              }));
              if (hydrated.length > 0) {
                setMessages(hydrated);
              }
            } else if (data.type === "cleared") {
              // Confirmacion de limpieza: vaciar UI para que reaparezca
              // el empty state con los chips de sugerencias.
              setMessages([]);
            } else if (data.type === "processing") {
              setIsProcessing(true);
            } else if (data.type === "response") {
              setIsProcessing(false);
              setMessages(prev => [...prev, {
                id: Date.now(),
                role: "assistant",
                content: data.content,
                intent: data.intent,
                demoLimitReached: !!data.demo_limit_reached,
              }]);
              // Modo demo: chip "te quedan N preguntas" y, si se acabaron, lock + CTA
              if (typeof data.messages_remaining === "number") {
                setMessagesRemaining(data.messages_remaining);
              }
              if (data.demo_limit_reached) {
                setDemoLimitReached(true);
                setMessagesRemaining(0);
              }
            } else if (data.type === "error") {
              setIsProcessing(false);
              const content = `Error: ${data.message}`;
              if (ready) addErrorBubble(content);
              else pendingError = content;
            }
          } catch (e) {
            console.error("Error parsing message:", e);
          }
        };

        ws.onerror = (error) => {
          console.error("Habi WebSocket error:", error);
        };

        ws.onclose = (event) => {
          // Cierre del socket de un edificio anterior o de la pantalla que se
          // desmontó: no toca el estado actual.
          if (cancelled || wsRef.current !== ws) return;
          devLog("Habi WebSocket cerrado, code:", event.code);
          setIsConnected(false);

          // 4003: el servidor rechazó la conexión por falta de acceso al
          // edificio. Reintentar no cambia nada; el aviso de arriba alcanza
          // (sin la burbuja de error repetida).
          if (event.code === 4003) {
            setStopped("noAccess");
            setError("errors.noAccess");
            return;
          }
          if (pendingError) addErrorBubble(pendingError);
          // Cualquier otro cierre que no pedimos nosotros: reconectar con
          // espera creciente (3 s, 6 s, 12 s… hasta 60 s) y un tope de intentos.
          const attempt = reconnectAttemptsRef.current;
          if (attempt >= 8) {
            setStopped("failed");
            setError("errors.connectFailed");
            return;
          }
          reconnectAttemptsRef.current = attempt + 1;
          const delay = Math.min(60000, 3000 * 2 ** attempt) + Math.floor(Math.random() * 1000);
          devLog(`Intentando reconectar en ${Math.round(delay / 1000)} segundos...`);
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            if (!cancelled) connectWebSocket();
          }, delay);
        };

      } catch (e) {
        console.error("Error creando WebSocket:", e);
        setStopped("failed");
        setError("errors.connectFailed");
      }
    };

    connectWebSocket();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      setIsConnected(false);
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
        wsRef.current = null;
      }
    };
  }, [selectedBuilding, effEmail, effUserid]);

  // Enviar mensaje
  const sendMessage = () => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    
    const messagePayload = {
      type: "message",
      content: input.trim(),
      user_email: auth.currentUser?.email || "",
    };
    // Enrich with building context if available
    if (contextRef.current) {
      messagePayload.context = contextRef.current;
    }
    wsRef.current.send(JSON.stringify(messagePayload));

    setInput("");
    setIsProcessing(true);
  };

  // Enviar sugerencia rápida
  const sendSuggestion = (text) => {
    if (!text.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: text.trim()
    };

    setMessages(prev => [...prev, userMessage]);

    wsRef.current.send(JSON.stringify({
      type: "message",
      content: text.trim(),
      user_email: auth.currentUser?.email || ""
    }));

    setIsProcessing(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Render assistant content with basic formatting
  const renderAssistantContent = (content) => {
    if (!content) return null;
    // Split by lines and apply basic formatting
    const lines = content.split("\n");
    return lines.map((line, i) => {
      // Bold text: **text**
      const formattedLine = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      const safeLine = sanitizeHTML(formattedLine);
      // Bullet points
      if (line.trim().startsWith("- ") || line.trim().startsWith("• ")) {
        const safeBullet = sanitizeHTML(formattedLine.replace(/^[\s]*[-•]\s*/, ""));
        return (
          <div key={i} style={{ paddingLeft: 12, position: "relative", marginBottom: 4 }}>
            <span style={{ position: "absolute", left: 0 }}>•</span>
            <span dangerouslySetInnerHTML={{ __html: safeBullet }} />
          </div>
        );
      }
      // Headers: lines starting with #
      if (line.trim().startsWith("# ")) {
        return <div key={i} style={{ fontWeight: 400, fontSize: 16, marginTop: 8, marginBottom: 4 }}>{line.replace(/^#\s*/, "")}</div>;
      }
      // Empty lines as spacing
      if (line.trim() === "") {
        return <div key={i} style={{ height: 8 }} />;
      }
      return <div key={i} dangerouslySetInnerHTML={{ __html: safeLine }} />;
    });
  };

  // Estado visible de la conexión: un corte definitivo (sin acceso o sin
  // conexión tras los reintentos) no se muestra como "Reconectando…".
  const connKey = isConnected
    ? "online"
    : stopped === "noAccess"
      ? "noAccess"
      : stopped === "failed"
        ? "offline"
        : hasConnectedOnce ? "reconnecting" : "connecting";
  const connPending = connKey === "connecting";

  // ─── Versión mobile: estilo app "Radial" (reskin del contenedor de chat) ─────
  if (isMobile) {
    const connLabel = t(`status.${connKey}`);
    const connTone = isConnected ? "green" : (connPending ? "amber" : "red");
    // Barra de input fija por encima del FAB del menú radial.
    const inputBarBottom = "calc(100px + env(safe-area-inset-bottom, 0px))";

    return (
      <div
        style={{
          background: appColors.page,
          minHeight: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 480,
            display: "flex",
            flexDirection: "column",
            padding: `16px ${appSpacing.screenPad}px 0`,
            boxSizing: "border-box",
            minWidth: 0,
          }}
        >
          <AppHeader
            title="Habi"
            subtitle={selectedBuilding ? t("header.subtitleWithBuilding", { building: selectedBuilding.name }) : t("header.subtitleDefault")}
            onBack={goBack}
            right={<Badge tone={connTone} dot>{connLabel}</Badge>}
          />

          {/* Botón nueva conversación */}
          {messages.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <AppButton
                variant="outline"
                disabled={!isConnected || messages.length === 0}
                onClick={() => {
                  if (!isConnected) return;
                  const ok = window.confirm(
                    t("actions.newConversationConfirm")
                  );
                  if (!ok) return;
                  try {
                    wsRef.current?.send(JSON.stringify({ type: "clear_conversation" }));
                  } catch (e) {
                    console.error("Habi: error mandando clear_conversation:", e);
                  }
                  setMessages([]);
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: 12.5,
                  borderRadius: 999,
                  color: appColors.ink2,
                }}
              >
                {t("actions.newConversation")}
              </AppButton>
            </div>
          )}

          {/* Área de mensajes */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 14,
              paddingBottom: `calc(210px + env(safe-area-inset-bottom, 0px))`,
              minWidth: 0,
            }}
          >
            {error && (
              <div
                style={{
                  background: appColors.surface,
                  border: `1px solid ${appColors.line}`,
                  borderLeft: `3px solid ${appColors.red}`,
                  borderRadius: 16,
                  padding: "14px 16px",
                  color: appColors.red,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  ...appType.body,
                }}
              >
                <span style={{ minWidth: 0, wordBreak: "break-word" }}>{t(error)}</span>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: appColors.red,
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    padding: "8px 14px",
                    fontFamily: appType.fontFamily,
                    fontSize: 13,
                    fontWeight: 400,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("actions.retry")}
                </button>
              </div>
            )}

            {messages.length === 0 && !error && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  paddingTop: 4,
                }}
              >
                {/* Sugerencias como tiles ricos (grilla que envuelve, sin scroll horizontal) */}
                <SectionLabel style={{ marginBottom: 12 }}>{t("empty.askSomething")}</SectionLabel>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: appSpacing.gridGap,
                    minWidth: 0,
                  }}
                >
                  {suggestions.map((suggestion, index) => {
                    const disabled = !isConnected || isProcessing;
                    return (
                      <button
                        key={index}
                        onClick={() => sendSuggestion(suggestion)}
                        disabled={disabled}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          width: "100%",
                          minWidth: 0,
                          textAlign: "left",
                          padding: 14,
                          borderRadius: appRadius.tile,
                          border: `1px solid ${appColors.line2}`,
                          background: appColors.surface,
                          boxShadow: appShadows.soft,
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.55 : 1,
                        }}
                      >
                        <IconBox icon={<Send size={16} />} size={38} />
                        <span
                          style={{
                            ...appType.body,
                            flex: 1,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {suggestion}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 8,
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                {msg.role === "assistant" && (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      minWidth: 30,
                      borderRadius: 10,
                      background: appColors.blue,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Bot size={15} />
                  </div>
                )}
                <div
                  style={{
                    maxWidth: "78%",
                    minWidth: 0,
                    padding: "11px 14px",
                    borderRadius: 16,
                    ...appType.body,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    ...(msg.role === "user"
                      ? {
                          background: appColors.blue,
                          color: "#fff",
                          borderBottomRightRadius: 5,
                        }
                      : msg.isError
                      ? {
                          background: appColors.surface,
                          color: appColors.red,
                          border: `1px solid ${appColors.line}`,
                          borderLeft: `3px solid ${appColors.red}`,
                          borderBottomLeftRadius: 5,
                        }
                      : {
                          background: appColors.surface,
                          color: appColors.ink,
                          border: `1px solid ${appColors.line2}`,
                          boxShadow: appShadows.soft,
                          borderBottomLeftRadius: 5,
                        }),
                  }}
                >
                  {msg.role === "assistant" ? renderAssistantContent(msg.content) : msg.content}
                </div>
                {msg.role === "user" && (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      minWidth: 30,
                      borderRadius: 10,
                      background: appColors.surface2,
                      color: appColors.blue,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <User size={15} />
                  </div>
                )}
              </div>
            ))}

            {isProcessing && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <div
                  style={{
                    width: 30,
                    height: 30,
                    minWidth: 30,
                    borderRadius: 10,
                    background: appColors.blue,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Bot size={15} />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "11px 14px",
                    background: appColors.surface,
                    border: `1px solid ${appColors.line2}`,
                    boxShadow: appShadows.soft,
                    borderRadius: 16,
                    borderBottomLeftRadius: 5,
                    ...appType.bodySm,
                  }}
                >
                  <Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} />
                  <span>{t("typing")}</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Barra fija: chips demo + input, por encima del FAB del menú radial */}
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: inputBarBottom,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 30,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 480,
                padding: `0 ${appSpacing.screenPad}px`,
                boxSizing: "border-box",
                pointerEvents: "auto",
              }}
            >
              {/* Chip "te quedan N preguntas" */}
              {typeof messagesRemaining === "number" && !demoLimitReached && (
                <div
                  style={{
                    marginBottom: 8,
                    padding: "8px 14px",
                    background: appColors.surface2,
                    borderRadius: 14,
                    textAlign: "center",
                    ...appType.bodySm,
                    color: appColors.blue,
                  }}
                >
                  {t("demo.remainingBefore")} {messagesRemaining} {t("demo.remainingAfter", { count: messagesRemaining })}
                </div>
              )}

              {/* Banner de límite alcanzado + CTA */}
              {demoLimitReached && (
                <div
                  style={{
                    marginBottom: 8,
                    padding: "12px 14px",
                    background: appColors.surface,
                    border: `1px solid ${appColors.line}`,
                    borderLeft: `3px solid ${appColors.amber}`,
                    borderRadius: 16,
                    boxShadow: appShadows.soft,
                  }}
                >
                  <span style={{ ...appType.bodySm, color: appColors.amber, display: "block", marginBottom: 10 }}>
                    {t("demo.limitReached")}
                  </span>
                  <AppButton
                    block
                    disabled={contactingAdvisor}
                    onClick={async () => {
                      if (contactingAdvisor) return;
                      setContactingAdvisor(true);
                      try {
                        const { httpsCallable } = await import("firebase/functions");
                        const { functions } = await import("../firebase");
                        const fn = httpsCallable(functions, "requestDemoContact");
                        await fn({ preferredContactTime: "", comments: "Desde el chat de Habi" });
                        setMessages(prev => [...prev, {
                          id: Date.now(),
                          role: "assistant",
                          content: t("demo.contactSuccess"),
                        }]);
                      } catch (e) {
                        setMessages(prev => [...prev, {
                          id: Date.now(),
                          role: "assistant",
                          content: t("demo.contactError", { message: e.message || t("demo.errorGeneric") }),
                          isError: true,
                        }]);
                      } finally {
                        setContactingAdvisor(false);
                      }
                    }}
                  >
                    {contactingAdvisor ? t("demo.sending") : t("demo.contactAdvisor")}
                  </AppButton>
                </div>
              )}

              {/* Input */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  padding: 8,
                  background: appColors.surface,
                  border: `1px solid ${appColors.line}`,
                  borderRadius: 999,
                  boxShadow: appShadows.hero,
                }}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={demoLimitReached ? t("input.placeholderLimit") : t("input.placeholder")}
                  disabled={!isConnected || isProcessing || demoLimitReached}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: "10px 14px",
                    borderRadius: 999,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontFamily: appType.fontFamily,
                    fontSize: 14.5,
                    fontWeight: 400,
                    color: appColors.ink,
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || !isConnected || isProcessing || demoLimitReached}
                  aria-label={t("actions.send")}
                  style={{
                    width: 44,
                    height: 44,
                    minWidth: 44,
                    borderRadius: "50%",
                    background: appColors.blue,
                    color: "#fff",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    opacity: (!input.trim() || !isConnected || isProcessing || demoLimitReached) ? 0.5 : 1,
                  }}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Versión desktop (existente) ─────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.headerIcon}>
          <Bot size={22} />
        </div>
        <div style={styles.headerInfo}>
          <h1 style={styles.pageTitle}>Habi</h1>
          <p style={styles.pageSubtitle}>{t("header.pageSubtitle")}</p>
        </div>
      </div>

      {/* Chat Container */}
      <div style={styles.chatContainer}>
        {/* Chat Header */}
        <div style={styles.chatHeader}>
          <div style={styles.headerIcon}>
            <Bot size={22} />
          </div>
          <div style={styles.headerText}>
            <h2 style={styles.headerTitle}>{t("header.virtualAssistant")}</h2>
            <p style={styles.headerSubtitle}>
              {isConnected ? t("status.connected") : t(`status.${connKey}`)}
              {selectedBuilding ? ` • ${selectedBuilding.name}` : ""}
            </p>
          </div>
          <div style={{
            ...styles.statusBadge,
            backgroundColor: isConnected ? "#dcfce7" : (connPending ? "#fef9c3" : "#fef2f2"),
            color: isConnected ? "#16a34a" : (connPending ? "#a16207" : "#dc2626")
          }}>
            <div style={{
              ...styles.statusDot,
              backgroundColor: isConnected ? "#16a34a" : (connPending ? "#a16207" : "#dc2626")
            }} />
            {t(`status.${connKey}`)}
          </div>
          <button
            type="button"
            style={styles.newConversationBtn}
            disabled={!isConnected || messages.length === 0}
            onClick={() => {
              if (!isConnected) return;
              const ok = window.confirm(
                t("actions.newConversationConfirm")
              );
              if (!ok) return;
              try {
                wsRef.current?.send(JSON.stringify({ type: "clear_conversation" }));
              } catch (e) {
                console.error("Habi: error mandando clear_conversation:", e);
              }
              setMessages([]);
            }}
            title={t("actions.newConversation")}
          >
            {t("actions.newConversation")}
          </button>
        </div>

        {/* Messages Area */}
        <div style={styles.messagesContainer}>
          {error && (
            <div style={styles.errorBanner}>
              <span>{t(error)}</span>
              <button
                onClick={() => window.location.reload()}
                style={styles.retryButton}
              >
                {t("actions.retry")}
              </button>
            </div>
          )}

          {messages.length === 0 && !error && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIconWrapper}>
                <Bot size={40} color="#0080ff" />
              </div>
              <h3 style={styles.emptyTitle}>{t("empty.title")}</h3>
              <p style={styles.emptyText}>
                {t("empty.text")}
              </p>
              <div style={styles.suggestionsContainer}>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => sendSuggestion(suggestion)}
                    disabled={!isConnected || isProcessing}
                    style={{
                      ...styles.suggestionChip,
                      opacity: (!isConnected || isProcessing) ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (isConnected && !isProcessing) {
                        e.target.style.background = "#e8f4ff";
                        e.target.style.borderColor = "#0080ff";
                        e.target.style.color = "#0080ff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#f8fafc";
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.color = "#374151";
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                ...styles.messageWrapper,
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start"
              }}
            >
              {msg.role === "assistant" && (
                <div style={styles.avatarBot}>
                  <Bot size={16} />
                </div>
              )}
              <div style={{
                ...styles.messageBubble,
                ...(msg.role === "user" ? styles.userBubble : styles.assistantBubble),
                ...(msg.isError ? styles.errorBubble : {})
              }}>
                {msg.role === "assistant" ? renderAssistantContent(msg.content) : msg.content}
              </div>
              {msg.role === "user" && (
                <div style={styles.avatarUser}>
                  <User size={16} />
                </div>
              )}
            </div>
          ))}

          {isProcessing && (
            <div style={styles.messageWrapper}>
              <div style={styles.avatarBot}>
                <Bot size={16} />
              </div>
              <div style={styles.typingIndicator}>
                <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                <span>{t("typing")}</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Chip "te quedan N preguntas" — visible solo en demo */}
        {typeof messagesRemaining === "number" && !demoLimitReached && (
          <div style={{
            padding: "6px 16px",
            background: "#e8f4ff",
            color: "#0066cc",
            fontSize: 13,
            textAlign: "center",
            borderTop: "1px solid #d1e7ff",
          }}>
            {t("demo.remainingBefore")} <b style={{ fontWeight: 400 }}>{messagesRemaining}</b> {t("demo.remainingAfter", { count: messagesRemaining })}
          </div>
        )}

        {/* Banner de limite alcanzado + CTA "Contactar asesor" */}
        {demoLimitReached && (
          <div style={{
            padding: "12px 16px",
            background: "#fef3c7",
            borderTop: "1px solid #fde68a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}>
            <span style={{ color: "#92400e", fontSize: 14 }}>
              {t("demo.limitReached")}
            </span>
            <button
              onClick={async () => {
                if (contactingAdvisor) return;
                setContactingAdvisor(true);
                try {
                  const { httpsCallable } = await import("firebase/functions");
                  const { functions } = await import("../firebase");
                  const fn = httpsCallable(functions, "requestDemoContact");
                  await fn({ preferredContactTime: "", comments: "Desde el chat de Habi" });
                  setMessages(prev => [...prev, {
                    id: Date.now(),
                    role: "assistant",
                    content: t("demo.contactSuccess"),
                  }]);
                } catch (e) {
                  setMessages(prev => [...prev, {
                    id: Date.now(),
                    role: "assistant",
                    content: t("demo.contactError", { message: e.message || t("demo.errorGeneric") }),
                    isError: true,
                  }]);
                } finally {
                  setContactingAdvisor(false);
                }
              }}
              disabled={contactingAdvisor}
              style={{
                padding: "8px 16px",
                background: "#0080ff",
                color: "white",
                border: "none",
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 400,
                fontFamily: "inherit",
                cursor: contactingAdvisor ? "wait" : "pointer",
                opacity: contactingAdvisor ? 0.6 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {contactingAdvisor ? t("demo.sending") : t("demo.contactAdvisor")}
            </button>
          </div>
        )}

        {/* Input Area */}
        <div style={styles.inputContainer}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={demoLimitReached ? t("input.placeholderLimit") : t("input.placeholder")}
            style={styles.input}
            disabled={!isConnected || isProcessing || demoLimitReached}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || !isConnected || isProcessing || demoLimitReached}
            style={{
              ...styles.sendButton,
              opacity: (!input.trim() || !isConnected || isProcessing || demoLimitReached) ? 0.5 : 1
            }}
          >
            <Send size={20} />
          </button>
        </div>
      </div>

    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 70px)",
    backgroundColor: "#f5f7fa",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    padding: "24px"
  },
  pageHeader: {
    background: "white",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  headerInfo: {
    flex: 1
  },
  pageTitle: {
    fontSize: "20px",
    fontWeight: 400,
    margin: 0,
    color: "#0080ff"
  },
  pageSubtitle: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "2px 0 0 0",
    fontWeight: 400
  },
  chatContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    minHeight: 0
  },
  chatHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px 20px",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e5e7eb"
  },
  headerIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "12px",
    backgroundColor: "#0080ff",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  headerText: {
    flex: 1
  },
  headerTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 400,
    color: "#1a1a1a"
  },
  headerSubtitle: {
    margin: 0,
    fontSize: "13px",
    color: "#6b7280"
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 400
  },
  statusDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%"
  },
  newConversationBtn: {
    marginLeft: "10px",
    padding: "6px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#4b5563",
    fontSize: "12px",
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    backgroundColor: "#fafbfc"
  },
  errorBanner: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    padding: "14px 16px",
    color: "#dc2626",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "14px"
  },
  retryButton: {
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: "13px"
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "48px"
  },
  emptyIconWrapper: {
    width: "80px",
    height: "80px",
    borderRadius: "20px",
    backgroundColor: "#e8f4ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px"
  },
  emptyTitle: {
    margin: "0 0 8px 0",
    fontSize: "20px",
    fontWeight: 400,
    color: "#1a1a1a"
  },
  emptyText: {
    margin: 0,
    fontSize: "14px",
    color: "#6b7280",
    maxWidth: "320px",
    lineHeight: 1.6
  },
  suggestionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 20,
    justifyContent: "center",
    maxWidth: 440,
  },
  suggestionChip: {
    padding: "8px 16px",
    borderRadius: 20,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    color: "#374151",
    fontSize: 13,
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "inherit",
  },
  messageWrapper: {
    display: "flex",
    alignItems: "flex-end",
    gap: "10px"
  },
  avatarBot: {
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    backgroundColor: "#0080ff",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  avatarUser: {
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    backgroundColor: "#e8f4ff",
    color: "#0080ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  messageBubble: {
    maxWidth: "70%",
    padding: "12px 16px",
    borderRadius: "14px",
    fontSize: "14px",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap"
  },
  userBubble: {
    backgroundColor: "#0080ff",
    color: "white",
    borderBottomRightRadius: "4px"
  },
  assistantBubble: {
    backgroundColor: "white",
    color: "#1a1a1a",
    borderBottomLeftRadius: "4px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb"
  },
  errorBubble: {
    backgroundColor: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca"
  },
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 16px",
    backgroundColor: "white",
    borderRadius: "14px",
    borderBottomLeftRadius: "4px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb",
    fontSize: "14px",
    color: "#6b7280"
  },
  inputContainer: {
    display: "flex",
    gap: "12px",
    padding: "16px 20px",
    backgroundColor: "white",
    borderTop: "1px solid #e5e7eb"
  },
  input: {
    flex: 1,
    padding: "14px 18px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    fontSize: "14px",
    outline: "none",
    fontFamily: "inherit",
    backgroundColor: "#f8f9fa",
    transition: "all 0.2s ease"
  },
  sendButton: {
    width: "50px",
    height: "50px",
    borderRadius: "12px",
    backgroundColor: "#0080ff",
    color: "white",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(0, 128, 255, 0.3)"
  }
};

export default Habi;
